/**
 * F-02E-1 — ShareLinkCreateModal
 *
 * Authenticated owner/operator modal for minting a view-only share link.
 *
 * Two-phase modal:
 *   1. Compose: label / expiry / passphrase / exposed-section toggles.
 *   2. Success: the full `https://…/share/<token>` URL with a Copy button.
 *
 * Important: the raw URL is shown ONCE. The backend never returns the raw
 * token again from any other endpoint (only `token_prefix` for owner
 * inspection). After the user dismisses the success view, the URL is
 * gone forever — they must mint a new link if they lose it.
 */

import { useEffect, useState } from 'react';
import {
  createShareLink,
  buildShareUrlFromPath,
  SHARE_LINK_EXPOSED_SECTION_OPTIONS,
  SHARE_LINK_SECTION_LABELS,
  SHARE_LINK_DEFAULT_EXPIRY_DAYS,
  SHARE_LINK_MAX_EXPIRY_DAYS,
  type ShareLinkExposedSection,
  type ShareLinkCreated,
  type CreateShareLinkPayload,
} from '../../services/shareApi';

/** F-02E-3C: strong warning shown when never-expire is enabled. */
const NEVER_EXPIRE_WARNING =
  'This link never expires. Anyone with the URL keeps read-only access ' +
  'indefinitely until you revoke it. Prefer a passphrase and revoke when ' +
  'no longer needed.';

/** FL-1: warning shown when a field link has no passphrase. */
const FIELD_NO_PASSPHRASE_WARNING =
  'This link allows edits. Without a passphrase, anyone who obtains the ' +
  'URL can update indications. Strongly recommended: set a passphrase.';
import { ApiError } from '../../services/apiFetch';
import './ShareLinkCreateModal.css';

interface Props {
  siteId: string;
  siteName?: string | null;
  /** Called after dismiss (with or without a successful create). */
  onClose: () => void;
  /** Called after a successful create so the manager panel can refresh. */
  onCreated?: (link: ShareLinkCreated) => void;
}

const EXPIRY_DAY_PRESETS = [7, 30, 60, 90] as const;

export default function ShareLinkCreateModal({
  siteId,
  siteName,
  onClose,
  onCreated,
}: Props) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'view' | 'field'>('view');
  const [expiryDays, setExpiryDays] = useState<number>(
    SHARE_LINK_DEFAULT_EXPIRY_DAYS,
  );
  const [neverExpire, setNeverExpire] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [exposed, setExposed] = useState<Set<ShareLinkExposedSection>>(
    () => new Set(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ShareLinkCreated | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleSection = (section: ShareLinkExposedSection) => {
    setExposed((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!siteId) {
      setError('No project is selected. Open a project before creating a link.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const trimmedLabel = label.trim();
      const trimmedPass = passphrase;
      const payload: CreateShareLinkPayload = {
        label: trimmedLabel || null,
        passphrase: trimmedPass ? trimmedPass : null,
        exposedSections: Array.from(exposed) as ShareLinkExposedSection[],
      };
      if (kind === 'field') payload.kind = 'field';
      // Never-expire and a finite expiry are mutually exclusive (the backend
      // Zod refine rejects both). Send exactly one. Field links are always
      // finite (the kind toggle clears neverExpire and hides the checkbox).
      if (neverExpire && kind !== 'field') {
        payload.neverExpires = true;
      } else {
        payload.expiresAt = new Date(
          Date.now() + expiryDays * 24 * 60 * 60 * 1000,
        ).toISOString();
      }
      const result = await createShareLink(siteId, payload);
      setCreated(result);
      onCreated?.(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to create share link');
      } else {
        setError('Failed to create share link');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    const url = buildShareUrlFromPath(created.share_path);
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setCopyState('error');
    }
  };

  const shareUrl = created ? buildShareUrlFromPath(created.share_path) : '';

  return (
    <div
      className="share-create-modal__backdrop"
      onClick={onClose}
      data-testid="share-create-modal-backdrop"
    >
      <div
        className="share-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-create-modal-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-create-modal"
      >
        <header className="share-create-modal__header">
          <h2
            className="share-create-modal__title"
            id="share-create-modal-title"
          >
            {created
              ? 'Share link created'
              : kind === 'field'
              ? 'Create field link'
              : 'Create view-only share link'}
          </h2>
          <button
            type="button"
            className="share-create-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {!created && (
          <form
            className="share-create-modal__body"
            onSubmit={handleSubmit}
            data-testid="share-create-form"
          >
            {siteName && (
              <p className="share-create-modal__site" data-testid="share-create-site">
                Project: <strong>{siteName}</strong>
              </p>
            )}

            {/* FL-1: link kind — view-only (default) or field editing. */}
            <div className="share-create-modal__field">
              <span className="share-create-modal__label">Link type</span>
              <div
                className="share-create-modal__expiry-row"
                role="radiogroup"
                aria-label="Link type"
              >
                {(['view', 'field'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={kind === k}
                    className={
                      'share-create-modal__chip' +
                      (kind === k ? ' share-create-modal__chip--active' : '')
                    }
                    onClick={() => {
                      setKind(k);
                      // Field links must have a finite expiry — the backend
                      // rejects neverExpires for kind='field'.
                      if (k === 'field') setNeverExpire(false);
                    }}
                    data-testid={`share-create-kind-${k}`}
                  >
                    {k === 'view' ? 'View only' : 'Field editing'}
                  </button>
                ))}
              </div>
            </div>

            <p className="share-create-modal__hint">
              {kind === 'field' ? (
                <>
                  Technicians with this link can <strong>update indication
                  notes and status</strong> from the field — every change is
                  attributed to the name they enter and can be undone from the
                  operations history. Everything else stays read-only. The full
                  share URL is shown <strong>only once</strong> right after
                  creation — copy it before closing.
                </>
              ) : (
                <>
                  Visitors receive a read-only snapshot of the project (map, layout,
                  orthophotos, indications). Other sections are off unless you
                  opt-in below. The full share URL is shown <strong>only once</strong>{' '}
                  right after creation — copy it before closing.
                </>
              )}
            </p>

            {error && (
              <div className="share-create-modal__alert share-create-modal__alert--error" role="alert" data-testid="share-create-error">
                {error}
              </div>
            )}

            <label className="share-create-modal__field">
              <span className="share-create-modal__label">Label (optional)</span>
              <input
                type="text"
                className="share-create-modal__input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Investor preview, Customer X"
                maxLength={200}
                data-testid="share-create-label"
              />
            </label>

            <div className="share-create-modal__field">
              {/* FL-1: never-expire is view-only — writable links must rotate. */}
              {kind !== 'field' && (
                <label
                  className="share-create-modal__never-expire"
                  data-testid="share-create-never-expire-label"
                >
                  <input
                    type="checkbox"
                    checked={neverExpire}
                    onChange={(e) => setNeverExpire(e.target.checked)}
                    data-testid="share-create-never-expire"
                  />
                  <span>Never expires</span>
                </label>
              )}

              {neverExpire ? (
                <div
                  className="share-create-modal__alert share-create-modal__alert--warn"
                  role="alert"
                  data-testid="share-create-never-expire-warning"
                >
                  {NEVER_EXPIRE_WARNING}
                </div>
              ) : (
                <>
                  <span className="share-create-modal__label">Expires in</span>
                  <div
                    className="share-create-modal__expiry-row"
                    role="radiogroup"
                    aria-label="Expiry in days"
                  >
                    {EXPIRY_DAY_PRESETS.map((days) => (
                      <button
                        key={days}
                        type="button"
                        role="radio"
                        aria-checked={expiryDays === days}
                        className={
                          'share-create-modal__chip' +
                          (expiryDays === days ? ' share-create-modal__chip--active' : '')
                        }
                        onClick={() => setExpiryDays(days)}
                        data-testid={`share-create-expiry-${days}`}
                      >
                        {days} days
                      </button>
                    ))}
                    <input
                      type="number"
                      className="share-create-modal__input share-create-modal__input--days"
                      min={1}
                      max={SHARE_LINK_MAX_EXPIRY_DAYS}
                      value={expiryDays}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isFinite(v)) return;
                        setExpiryDays(
                          Math.max(1, Math.min(SHARE_LINK_MAX_EXPIRY_DAYS, v)),
                        );
                      }}
                      aria-label="Custom days"
                      data-testid="share-create-expiry-custom"
                    />
                  </div>
                  <span className="share-create-modal__hint share-create-modal__hint--small">
                    Max {SHARE_LINK_MAX_EXPIRY_DAYS} days. Backend enforces the
                    cap.
                  </span>
                </>
              )}
            </div>

            <label className="share-create-modal__field">
              <span className="share-create-modal__label">
                Passphrase (optional)
              </span>
              <input
                type="password"
                className="share-create-modal__input"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder="Leave blank for a no-passphrase link"
                autoComplete="new-password"
                maxLength={200}
                data-testid="share-create-passphrase"
              />
              <span className="share-create-modal__hint share-create-modal__hint--small">
                If set, visitors must enter it before viewing. The passphrase
                is never shown again — store it separately if you forget.
              </span>
              {neverExpire && !passphrase.trim() && (
                <span
                  className="share-create-modal__hint share-create-modal__hint--small share-create-modal__hint--recommend"
                  data-testid="share-create-passphrase-recommend"
                >
                  Recommended: add a passphrase for a never-expiring link.
                </span>
              )}
              {kind === 'field' && !passphrase.trim() && (
                <span
                  className="share-create-modal__alert share-create-modal__alert--warn"
                  role="alert"
                  data-testid="share-create-field-passphrase-warning"
                >
                  {FIELD_NO_PASSPHRASE_WARNING}
                </span>
              )}
            </label>

            <fieldset className="share-create-modal__field share-create-modal__sections">
              <legend className="share-create-modal__label">
                Optional sections to expose
              </legend>
              <span className="share-create-modal__hint share-create-modal__hint--small">
                Core project, map, layout, photos are always included. Toggle
                additional sections below.
              </span>
              <div className="share-create-modal__section-grid">
                {SHARE_LINK_EXPOSED_SECTION_OPTIONS.map((section) => {
                  const checked = exposed.has(section);
                  return (
                    <label
                      key={section}
                      className={
                        'share-create-modal__section-option' +
                        (checked ? ' share-create-modal__section-option--checked' : '')
                      }
                      data-testid={`share-create-section-${section}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSection(section)}
                        aria-label={SHARE_LINK_SECTION_LABELS[section]}
                      />
                      <span>{SHARE_LINK_SECTION_LABELS[section]}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="share-create-modal__actions">
              <button
                type="button"
                className="share-create-modal__btn share-create-modal__btn--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="share-create-modal__btn share-create-modal__btn--primary"
                disabled={submitting || !siteId}
                data-testid="share-create-submit"
              >
                {submitting ? 'Creating…' : 'Create share link'}
              </button>
            </div>
          </form>
        )}

        {created && (
          <div className="share-create-modal__body" data-testid="share-create-success">
            <p className="share-create-modal__hint">
              The link is ready. <strong>Copy the URL now</strong> — it cannot
              be displayed again.
            </p>

            <div className="share-create-modal__url-row">
              <input
                type="text"
                className="share-create-modal__input share-create-modal__input--url"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.currentTarget.select()}
                data-testid="share-create-url"
                aria-label="Share URL"
              />
              <button
                type="button"
                className="share-create-modal__btn share-create-modal__btn--primary"
                onClick={handleCopy}
                data-testid="share-create-copy"
              >
                {copyState === 'copied'
                  ? 'Copied!'
                  : copyState === 'error'
                  ? 'Copy failed'
                  : 'Copy URL'}
              </button>
            </div>

            <dl className="share-create-modal__summary">
              <div>
                <dt>Type</dt>
                <dd data-testid="share-create-success-kind">
                  {created.kind === 'field' ? 'Field editing' : 'View only'}
                </dd>
              </div>
              <div>
                <dt>Label</dt>
                <dd>{created.label || '—'}</dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd data-testid="share-create-success-expiry">
                  {created.never_expires
                    ? 'Never'
                    : new Date(created.expires_at).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Passphrase</dt>
                <dd>{created.has_passphrase ? 'Required' : 'None'}</dd>
              </div>
              <div>
                <dt>Exposed sections</dt>
                <dd>
                  {created.exposed_sections && created.exposed_sections.length
                    ? created.exposed_sections.join(', ')
                    : 'Core only'}
                </dd>
              </div>
            </dl>

            <div className="share-create-modal__actions">
              <button
                type="button"
                className="share-create-modal__btn share-create-modal__btn--primary"
                onClick={onClose}
                data-testid="share-create-done"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
